import type { ArchbaseRemoteApiClient } from '@archbase/data'
import { injectable, inject } from 'inversify'
import { ARCHBASE_IOC_API_TYPE } from '@archbase/core'
import type {
  ApiKeyListResponse,
  ApiKeyCreatedDto,
  ApiKeyRecordDto,
  CreateApiKeyRequest,
} from '@domain/apiKey/ApiKeyDto'

const BASE = '/v1/admin/keys'

/**
 * CRUD de API keys contra `/v1/admin/keys`.
 *
 * O `X-Admin-Key` é injetado pelo interceptor do ContainerIOC — nenhum método daqui precisa
 * saber da credencial.
 */
@injectable()
export class DoclenzKeyService {
  constructor(@inject(ARCHBASE_IOC_API_TYPE.ApiClient) private client: ArchbaseRemoteApiClient) {}

  /** Lista todas as chaves. Vêm mascaradas — ver ApiKeyEntryDto. */
  async findAll(): Promise<ApiKeyListResponse> {
    return this.client.get(BASE)
  }

  /**
   * Cria uma chave. **A resposta traz a chave em claro e é a única chance de copiá-la** —
   * a listagem só devolve a versão mascarada.
   */
  async create(request: CreateApiKeyRequest): Promise<ApiKeyCreatedDto> {
    return this.client.post(BASE, request)
  }

  /**
   * Atualiza o registro de uma chave existente.
   *
   * @param keyOrId o `key_id` da listagem (ou a chave em claro, se ainda estiver em mãos)
   */
  async update(keyOrId: string, record: ApiKeyRecordDto): Promise<unknown> {
    return this.client.put(`${BASE}/${encodeURIComponent(keyOrId)}`, record)
  }

  /** Revoga a chave. Irreversível. Aceita o `key_id` da listagem. */
  async revoke(keyOrId: string): Promise<void> {
    return this.client.delete(`${BASE}/${encodeURIComponent(keyOrId)}`)
  }
}
